import { ArrowDropDown } from '@mui/icons-material';
import { Chip, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';

import { TICKET_STATUS_CARD_STYLES, TicketStatus } from 'data/constants/constants';
import useTranslate from 'hooks/useTranslate';
import { getTicketStatusLabelKey } from 'utils/ticket';

const STATUS_LIST = Object.values(TicketStatus);

interface TicketStatusChipProps {
    status: TicketStatus;
    onChangeStatus?: (newStatus: TicketStatus) => void;
    disabled?: boolean;
}

const TicketStatusChip = ({ status, onChangeStatus, disabled }: TicketStatusChipProps) => {
    const { translate } = useTranslate();
    const config = TICKET_STATUS_CARD_STYLES[status] ?? {
        label: status,
        color: 'text.secondary',
        bg: 'action.hover',
    };
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const interactive = Boolean(onChangeStatus) && !disabled;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!interactive) return;
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleSelect = (newStatus: TicketStatus) => {
        onChangeStatus?.(newStatus);
        setAnchorEl(null);
    };

    return (
        <>
            <Chip
                size="small"
                label={translate(getTicketStatusLabelKey(status))}
                deleteIcon={
                    interactive ? (
                        <ArrowDropDown sx={{ color: `${config.color} !important`, fontSize: 18 }} />
                    ) : undefined
                }
                onDelete={interactive ? handleClick : undefined}
                onClick={interactive ? handleClick : undefined}
                sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    color: config.color,
                    backgroundColor: config.bg,
                    height: 24,
                    cursor: interactive ? 'pointer' : 'default',
                }}
            />
            {interactive ? (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: { sx: { minWidth: 160, mt: 0.5 } },
                    }}
                >
                    {STATUS_LIST.map((item) => (
                        <MenuItem
                            key={item}
                            selected={item === status}
                            onClick={() => handleSelect(item)}
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemText>{translate(getTicketStatusLabelKey(item))}</ListItemText>
                        </MenuItem>
                    ))}
                </Menu>
            ) : null}
        </>
    );
};

export default TicketStatusChip;
